import { TimeZonesStore } from '@go-widgets/well-logs-widget';
import { format, parseISO } from 'date-fns';
import { action, computed, flow, makeObservable, observable, set } from 'mobx';
import { throttle } from 'throttle-debounce';

import { OperationAbortedError } from 'src/app/errors';
import { requireService } from 'src/packages/di';

import type { BroadcastObject, Wellbore, WellDto } from '../api/Wellbore.api';
import { WellboreApi } from '../api/Wellbore.api';

import { ImageBroadcastChannel } from './ImageBroadcastChannel';
import { ImageBroadcasts } from './ImageBroadcasts';
import type { AppParams, ImageTransformState, WidgetEvents, WidgetStateParams } from './types';
import { Wellbores } from './Wellbores';
import { WidgetStatePublisher } from './WidgetStatePublisher';

export class ImageBroadcastingWidgetState {
  wellboreApi: WellboreApi;

  wellbores: Wellbores;

  imageBroadcasts: ImageBroadcasts;

  timeZonesStore: TimeZonesStore;

  statePublisher: WidgetStatePublisher;

  @observable isTooltipVisible: boolean;

  @observable appParams: AppParams;

  @observable wellId: number;

  @observable wellData: WellDto | null = null;

  @observable status: 'initial' | 'loading' | 'ready' | 'failed' = 'initial';

  @observable isPlaying: boolean;

  @observable selectedBroadcast: ImageBroadcastChannel | null = null;

  @observable selectedWellbore: Wellbore | null = null;

  @observable private _selectedComment: string | null = null;

  @observable visiblePanels: {
    broadcasts: boolean;
    comments: boolean;
  };

  @observable commentsPanelRatio: number;

  @observable.ref imageTransformState: ImageTransformState | null;

  abortController: AbortController | null = null;

  throttledSetWell: (wellId: number) => void;

  throttledSetCommentsPanelRatioParams: (value: number) => void;

  constructor(
    appParams: AppParams,
    initState: Partial<WidgetStateParams>,
    private eventHandlers: WidgetEvents,
    private notifications = requireService('notifications'),
  ) {
    this.wellId = appParams.wellId;
    this.appParams = observable.object(appParams);

    this.statePublisher = new WidgetStatePublisher(initState, (state) => {
      this.eventHandlers?.onInternalStateChanged?.({ ...state });
    });
    this.wellboreApi = new WellboreApi();
    this.wellbores = new Wellbores(this.wellboreApi);
    this.imageBroadcasts = new ImageBroadcasts(this.wellboreApi);
    this.timeZonesStore = new TimeZonesStore(this.statePublisher.params.timeZone ?? 'user', (value) => {
      this.statePublisher.setParams({ timeZone: value });
    });
    this.isPlaying = initState.isPlaying ?? true;
    this.isTooltipVisible = initState.isTooltipVisible ?? true;
    this.visiblePanels = initState.visiblePanels ?? {
      broadcasts: true,
      comments: false,
    };
    this.commentsPanelRatio = initState.commentsPanelRatio ?? 0.5;
    this._selectedComment = initState.selectedComment ?? null;
    this.imageTransformState = initState.imageTransformState ?? null;

    makeObservable(this);

    this.throttledSetWell = throttle(800, this.setWell, {});
    this.throttledSetCommentsPanelRatioParams = throttle(
      200,
      (value: number) => {
        this.statePublisher.setParams({
          commentsPanelRatio: value,
        });
      },
      {},
    );

    this.init();
  }

  @computed
  get currentImageUrl() {
    if (!this.selectedBroadcast || !this.selectedBroadcast.info || !this.selectedBroadcast.image) {
      return null;
    }

    const formattedDate = format(parseISO(this.selectedBroadcast.info.updateTime), 'dd.MM.yyyy HH-mm-ss');

    return {
      url: this.selectedBroadcast.image,
      alt: [this.selectedBroadcast?.info.name, this.selectedWellbore?.name, this.wellData?.name, formattedDate].join(
        '_',
      ),
    };
  }

  @flow.bound
  async *init() {
    this.status = 'loading';

    try {
      await this.setWell(this.wellId);

      yield;
      if (this.eventHandlers.onInitializedSuccess) {
        this.eventHandlers.onInitializedSuccess();
        this.status = 'ready';
      }
    } catch (e) {
      yield;
      if (this.eventHandlers.onInitializationFailed) {
        if (e instanceof Error) {
          this.eventHandlers.onInitializationFailed(e.message, e);
        } else {
          this.eventHandlers.onInitializationFailed('Unexpected error');
        }
      }
      this.status = 'failed';
    }
  }

  @flow.bound
  async *setWell(wellId: number) {
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    this.updateWellData(wellId, this.abortController);

    this.selectedBroadcast = null;

    const wellbores = await this.wellbores.fetchWellbores(wellId, this.abortController);
    yield;

    const selected =
      wellbores.find((wellbore) => wellbore.id === this.statePublisher.params.wellboreId) ?? wellbores[0];
    if (selected) {
      this.setWellbore(selected);
    } else {
      this.imageBroadcasts.clear();
      if (this.eventHandlers.onTitleChanged) {
        this.eventHandlers.onTitleChanged('');
      }
    }
  }

  @flow.bound
  async *updateWellData(wellId: number, abortController: AbortController) {
    try {
      const well = await this.wellboreApi.getWell(wellId, abortController);
      yield;
      this.wellData = well;
      this.timeZonesStore.setWellTimeZoneByString(well.timeZone);
    } catch (e) {
      yield;
      if (!(e instanceof OperationAbortedError)) {
        this.notifications.showErrorMessageT('imageBroadcast:error.well');
      }
    }
  }

  @flow.bound
  async *setWellbore(wellbore: Wellbore) {
    if (!this.abortController) {
      return;
    }
    this.selectedWellbore = wellbore;

    if (this.eventHandlers.onWellboreChanged) {
      this.eventHandlers.onWellboreChanged(this.selectedWellbore.name);
    }

    const list = await this.imageBroadcasts.fetchBroadcasts(wellbore.id, this.abortController);
    yield;
    if (list[0]) {
      const prevBroadcastName = this.selectedBroadcast?.info?.name ?? null;
      if (prevBroadcastName) {
        const broadcast = list.find((b) => b.text === prevBroadcastName) ?? list[0];
        this.setBroadcast(broadcast);
      } else {
        const broadcast = list.find((b) => Number(b.id) === this.statePublisher.params.broadcastId) ?? list[0];
        this.setBroadcast(broadcast ?? list[0]);
      }
    } else {
      if (this.eventHandlers.onTitleChanged) {
        this.eventHandlers.onTitleChanged('');
      }
    }
  }

  @action.bound
  setBroadcast(broadcast: BroadcastObject) {
    const previousBroadcastId = this.selectedBroadcast?.id ?? null;
    const nextBroadcastId = Number(broadcast.id);

    if (this.statePublisher.params.broadcastId !== nextBroadcastId) {
      this.setImageTransformState(null);
    }

    if (this.selectedBroadcast) {
      this.selectedBroadcast.unsubscribe();
    }
    this.selectedBroadcast = new ImageBroadcastChannel(nextBroadcastId, this.wellboreApi);
    this.selectedBroadcast.update();
    if (this.isPlaying) {
      this.selectedBroadcast.subscribe();
    }
    if (previousBroadcastId !== null && previousBroadcastId !== nextBroadcastId) {
      this.setSelectedComment(null);
    }
    if (this.selectedWellbore) {
      this.statePublisher.setParams({
        broadcastId: nextBroadcastId,
        wellboreId: this.selectedWellbore.id,
      });
    }
    if (this.eventHandlers.onTitleChanged) {
      this.eventHandlers.onTitleChanged(broadcast.text);
    }
  }

  @action.bound
  setAppParams(params: Partial<AppParams>) {
    if (params.wellId && params.wellId !== this.appParams.wellId) {
      this.wellId = params.wellId;
      this.throttledSetWell(params.wellId);
    }
    set(this.appParams, params);
  }

  @action.bound
  setPlayingState(value: boolean) {
    this.isPlaying = value;
    this.statePublisher.setParams({
      isPlaying: value,
    });
    if (this.selectedBroadcast) {
      if (value) {
        this.selectedBroadcast.update();
        this.selectedBroadcast.subscribe();
      } else {
        this.selectedBroadcast.unsubscribe();
      }
    }
  }

  @action.bound
  setTooltipVisible(value: boolean) {
    this.isTooltipVisible = value;
    this.statePublisher.setParams({
      isTooltipVisible: value,
    });
  }

  @action.bound
  setVisiblePanels(value: Partial<{ broadcasts: boolean; comments: boolean }>) {
    const nextVisiblePanels = { ...this.visiblePanels, ...value };

    if (!nextVisiblePanels.broadcasts && !nextVisiblePanels.comments) {
      return;
    }

    set(this.visiblePanels, nextVisiblePanels);
    this.statePublisher.setParams({
      visiblePanels: nextVisiblePanels,
    });
  }

  @action.bound
  setCommentsPanelRatio(value: number) {
    this.commentsPanelRatio = value;
    this.throttledSetCommentsPanelRatioParams(value);
  }

  @action.bound
  setImageTransformState(value: ImageTransformState | null) {
    if (
      this.imageTransformState?.scale === value?.scale &&
      this.imageTransformState?.positionX === value?.positionX &&
      this.imageTransformState?.positionY === value?.positionY
    ) {
      return;
    }

    this.imageTransformState = value;
    this.statePublisher.setParams({ imageTransformState: value });
  }

  @action.bound
  setSelectedComment(value: string | null) {
    this._selectedComment = value;
    this.statePublisher.setParams({
      selectedComment: value,
    });
  }

  @computed
  get commentsOptions() {
    return (
      this.selectedBroadcast?.comments.map((comment) => ({
        label: comment.title,
        value: comment.title,
      })) ?? []
    );
  }

  @computed
  get selectedComment() {
    if (this._selectedComment) {
      const comments = this.selectedBroadcast?.comments ?? [];

      return comments.find((comment) => comment.title === this._selectedComment) ?? comments[0] ?? null;
    }

    return this.selectedBroadcast?.comments[0] ?? null;
  }

  @action.bound
  refetchBroadcasts() {
    if (this.selectedWellbore && this.abortController) {
      this.imageBroadcasts.fetchBroadcasts(this.selectedWellbore.id, this.abortController);
    }

    if (this.selectedBroadcast) {
      this.selectedBroadcast.update();
    }
  }

  @action.bound
  destroy() {
    if (this.selectedBroadcast) {
      this.selectedBroadcast.unsubscribe();
    }
  }
}
